import React, { useRef } from 'react';
import { isToday, PriorityOrderStatuses } from '@tradingblock/api';
import { BoldNumber, ResponsiveTd } from '@tradingblock/components';
import { Order, OrderEvent, OrderStatuses } from '@tradingblock/types';
import dayjs from 'dayjs';
import _ from 'lodash';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';
import useSWR from 'swr';
import { useApi } from '../../../context/Api';
import { accountIdSelector, subAccountIdSelector, useStateSelector } from '../../../data/global/dataSelectors';
import { getSpecificOrderMessages } from '../../../data/global/selectors/orderSelector';
import { fetchAccountOrders } from '../../../utilities/fetchAccountOrders';
import { OrderStatusToString } from '../util';
import { useSelector } from 'react-redux';
import { account } from '../../../data/global/selectors/accountSelectors';
/** Prefer ticker in the Symbol column; use "Symbol + Stock/Multileg" when the API sends those labels. */
function formatActivityOrderEventSymbol(event: Pick<OrderEvent, 'Symbol' | 'Description'>): string {
  const sym = event.Symbol != null ? String(event.Symbol).trim() : '';
  const desc = event.Description != null ? String(event.Description).trim() : '';
  if (desc === 'Stock' || desc === 'Multileg') {
    return sym ? `${sym} ${desc}` : desc;
  }
  return sym || desc;
}

export interface ActivityOrderEventsSubRowProps {
  row: Order;
  /** Must match the Activity list GetOrders request or the API may return zero Items for orderId-only queries. */
  dateFrom?: Date;
  dateTo?: Date;
}

export default function ActivityOrderEventsSubRow({ row, dateFrom, dateTo }: ActivityOrderEventsSubRowProps) {
  const api = useApi();
  const accountId = useStateSelector(accountIdSelector);
  const globalSubaccountId = useStateSelector(subAccountIdSelector);
  const messages = useStateSelector(getSpecificOrderMessages(row.OrderId));
  const hasSubAccounts = useSelector(account.hasSubAccounts);

  // Prefer the order's subaccount (matches list rows); global filter can differ when viewing "all" subaccounts.
  const subaccountIdForRequest =
    row.SubaccountId !== undefined && row.SubaccountId !== null ? row.SubaccountId : globalSubaccountId;

  /** Rows from failed placements or transient state may lack OrderId; never call GetOrders with a bogus id. */
  const hasValidOrderId = typeof row.OrderId === 'number' && Number.isFinite(row.OrderId);

  const orderDetailRequest = hasValidOrderId
    ? {
        orderId: row.OrderId,
        includeOrderEvents: true,
        dateFrom,
        dateTo,
        orderStatusId: '-1',
        workingOrdersOnly: false,
      }
    : null;

  const orderDetailSwrKey =
    orderDetailRequest &&
    ({
      accountId,
      subaccountId: subaccountIdForRequest,
      ...orderDetailRequest,
    });

  const { data, error, mutate } = useSWR(
    orderDetailSwrKey,
    orderDetailRequest
      ? () => fetchAccountOrders(api.order(accountId, subaccountIdForRequest), orderDetailRequest)
      : null
  );

  /** SWR already runs GetOrders on mount; calling mutate() on the first effect pass duplicated that request. */
  const skipMutateAfterInitialMessagesSnapshot = useRef(true);
  useDeepCompareEffect(() => {
    if (!hasValidOrderId) {
      return;
    }
    if (skipMutateAfterInitialMessagesSnapshot.current) {
      skipMutateAfterInitialMessagesSnapshot.current = false;
      return;
    }
    void mutate();
  }, [messages, hasValidOrderId, mutate, row.OrderId]);

  if (!hasValidOrderId) {
    return (
      <tr>
        <td colSpan={99}>No order ID for this row; events cannot be loaded.</td>
      </tr>
    );
  }

  // Loading data
  if (!error && !data) {
    return (
      <tr>
        <td>Loading...</td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td>Error...</td>
      </tr>
    );
  }

  const sortedOrderEvents =
    data && data.payload && data.payload.items[0] && data.payload.items[0].OrderEvents
      ? [...data.payload.items[0].OrderEvents].sort((a, b) => {
          const timeA = new Date(a.TimeStamp).getTime();
          const timeB = new Date(b.TimeStamp).getTime();

          if (timeA === timeB) {
            const statusA = PriorityOrderStatuses.findIndex(status => status === a.OrderStatus);
            const statusB = PriorityOrderStatuses.findIndex(status => status === b.OrderStatus);

            // Handle cases where status is not found (-1). Treat them as the lowest priority.
            const adjustedStatusA = statusA === -1 ? PriorityOrderStatuses.length : statusA;
            const adjustedStatusB = statusB === -1 ? PriorityOrderStatuses.length : statusB;

            // When timestamps are the same, sort by status priority
            return adjustedStatusB - adjustedStatusA;
          }
          return timeB - timeA; // Otherwise, sort by date descending
        })
      : [];

  if (sortedOrderEvents.length === 0) {
    return (
      <tr>
        <td>No events found.</td>
      </tr>
    );
  }

  return (
    <>
      {sortedOrderEvents.map((orderEvent, i) => {
        return (
          <tr
            style={{
              backgroundColor: 'rgb(12, 29, 63)',
            }}
            key={i}
          >
            <ResponsiveTd header="Symbol">{formatActivityOrderEventSymbol(orderEvent)}</ResponsiveTd>
            <ResponsiveTd header="OrderID">{row.OrderId}</ResponsiveTd>
            {hasSubAccounts && <ResponsiveTd header="Subaccount ID"></ResponsiveTd>}
            <ResponsiveTd header="Order Strategy"></ResponsiveTd>
            <ResponsiveTd header="OrderStatus">
              {OrderStatusToString((orderEvent.OrderStatus as unknown) as number)}
            </ResponsiveTd>
            <ResponsiveTd header="Bid"></ResponsiveTd>
            <ResponsiveTd header="Ask"></ResponsiveTd>
            <ResponsiveTd header="Limit">
              <BoldNumber value={orderEvent.Limit} currency />
            </ResponsiveTd>
            <ResponsiveTd header="FillPrice">
              {orderEvent.OrderStatus === OrderStatuses.Filled && <BoldNumber value={orderEvent.FillPrice} currency />}
            </ResponsiveTd>
            <ResponsiveTd header="Est. Commission"></ResponsiveTd>
            <ResponsiveTd header="FillQuantity">
              {(orderEvent.OrderStatus === OrderStatuses.PartiallyFilled ||
                orderEvent.OrderStatus === OrderStatuses.Filled) && (
                <>
                  <div className={`${orderEvent.OrderStatus === OrderStatuses.PartiallyFilled ? 'pending' : 'pos'}`}>
                    {orderEvent.OrderStatus === OrderStatuses.PartiallyFilled ? 'Partial Fill' : 'Filled'}
                  </div>
                  <span>{orderEvent.FillQuantity}</span>
                </>
              )}
            </ResponsiveTd>
            <ResponsiveTd header="TimeStamp">
              {dayjs(orderEvent.TimeStamp).format(
                isToday(orderEvent.TimeStamp) ? 'h:mm:ss.SSS A' : 'MM/DD/YY h:mm:ss.SSS A'
              )}
            </ResponsiveTd>
            <ResponsiveTd header="Action"></ResponsiveTd>
          </tr>
        );
      })}
    </>
  );
}
